<template>
  <div class="customer-service-contact-list">
    <h1 class="page-title">客服联系方式管理</h1>

    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>客服联系方式</h2>
        </div>
      </template>

      <div class="card-content" v-loading="loading">
        <el-form :model="formData" :rules="rules" ref="formRef" label-width="100px">
          <el-form-item label="联系方式" prop="title">
            <el-input v-model="formData.title" placeholder="请输入联系方式标题，例如：微信号" />
          </el-form-item>
          <el-form-item label="图片" prop="image">
            <!-- 图片大小提示 -->
            <el-alert
              title="图片上传提示"
              type="info"
              :closable="false"
              show-icon
              style="margin-bottom: 16px;"
            >
              <template #default>
                <div>基于当前服务器配置，建议您将上传的图片大小控制在200KB以下，以确保页面加载速度和最佳显示效果。</div>
              </template>
            </el-alert>
            
            <el-upload
              :file-list="fileList"
              :action="`${apiBaseUrl}/manage/upload/image`"
              list-type="picture-card"
              :auto-upload="true"
              :on-preview="handlePictureCardPreview"
              :on-remove="handleRemove"
              :on-success="handleUploadSuccess"
              :on-error="handleUploadError"
              :before-upload="beforeImageUpload"
              :data="{ type: 'other' }"
              :headers="{ 'Authorization': `Bearer ${authToken}` }"
              :limit="1"
              :on-exceed="handleExceed"
              :class="{ 'hide-upload-area': fileList.length > 0 }"
            >
              <template v-if="fileList.length < 1">
                <el-icon><Plus /></el-icon>
              </template>
              <template #tip>
                <div class="el-upload__tip">
                  只能上传jpg/png文件，且不超过20MB
                </div>
              </template>
            </el-upload>
            <el-dialog v-model="previewVisible" title="预览图片">
              <img style="width: 100%" :src="dialogImageUrl" alt="预览图片" />
            </el-dialog>
          </el-form-item>
          <el-form-item label="状态" prop="status">
            <el-switch v-model="formData.status" />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleSubmit" :loading="saving">保存</el-button>
          </el-form-item>
        </el-form>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { Upload, Plus } from '@element-plus/icons-vue';
import {
  fetchCustomerServiceContactList,
  addCustomerServiceContact,
  updateCustomerServiceContact
} from '@/api/customerServiceContact';

// 获取API基础URL
const apiBaseUrl = ref(process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api');

// 获取认证token
const getToken = () => {
  try {
    return localStorage.getItem('token') || '';
  } catch (e) {
    console.error('Failed to access localStorage:', e);
    return '';
  }
};

// 认证token
const authToken = ref(getToken());

const loading = ref(false);
const saving = ref(false);
const formRef = ref(null);
const contactId = ref(null);
const fileList = ref([]);
const previewVisible = ref(false);
const dialogImageUrl = ref('');

const formData = reactive({
  title: '客服联系方式',
  image: '',
  status: true
});

const rules = {
  image: [{ required: true, message: '请上传图片', trigger: 'change' }]
};

const fetchContact = async () => {
  loading.value = true;
  try {
    const res = await fetchCustomerServiceContactList({ page: 1, pageSize: 1 });
    if (res.data && res.data.length > 0) {
      const contact = res.data[0];
      contactId.value = contact.id;
      formData.title = contact.title;
      formData.image = contact.image_url || contact.image;
      formData.status = contact.status;
      if (formData.image) {
        fileList.value = [{
          uid: Date.now(),
          name: '客服联系方式',
          url: getFullImageUrl(formData.image),
          status: 'success'
        }];
      }
    }
  } catch (error) {
    ElMessage.error('获取客服联系方式失败');
  } finally {
    loading.value = false;
  }
};

// 图片上传前的验证
const beforeImageUpload = (file) => {
  const isJPG = file.type === 'image/jpeg' || file.type === 'image/png';
  const isLt20M = file.size / 1024 / 1024 < 20;
  
  if (!isJPG) {
    ElMessage.error('只能上传JPG/PNG格式的图片');
  }
  if (!isLt20M) {
    ElMessage.error('图片大小不能超过20MB');
  }
  
  return isJPG && isLt20M;
};

// 预览图片
const handlePictureCardPreview = (file) => {
  dialogImageUrl.value = file.url;
  previewVisible.value = true;
};

// 移除图片
const handleRemove = () => {
  fileList.value = [];
  formData.image = '';
};

// 处理超出上传限制
const handleExceed = (files, fileList) => {
  ElMessage.error('只能上传一张图片');
};

const handleUploadSuccess = (response, file, uploadedFileList) => {
  if (response.errno === 0 && response.data && response.data.length > 0) {
    // 获取相对路径，不添加完整域名
    let imgUrl = response.data[0].url;
    // 上传成功，设置图片URL（只保存相对路径）
    formData.image = imgUrl;
    // 覆盖文件列表，只保留最新上传的图片
    fileList.value = [{
      uid: file.uid,
      name: file.name,
      url: getFullImageUrl(imgUrl),
      status: 'success'
    }];
    ElMessage.success('图片上传成功');
  } else {
    ElMessage.error('图片上传失败: ' + (response.message || '未知错误'));
  }
};

const handleUploadError = (error, file, fileList) => {
  // 尝试从error对象中获取错误信息，处理各种可能的error类型
  let errorMessage = '网络错误';
  if (error) {
    if (typeof error === 'string') {
      errorMessage = error;
    } else if (error.response && error.response.data) {
      // 处理axios错误对象
      errorMessage = error.response.data.message || '未知错误';
    } else if (error.message) {
      errorMessage = error.message;
    } else if (error.statusText) {
      errorMessage = error.statusText;
    }
  }
  ElMessage.error('图片上传失败: ' + errorMessage);
};

// 获取完整的图片URL
const getFullImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  // 如果已经是完整URL，直接返回
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  // 从API基础URL中提取域名
  const domainMatch = apiBaseUrl.value.match(/^(https?:\/\/[^\/]+)/);
  const apiDomain = domainMatch ? domainMatch[1] : window.location.origin;
  // 拼接API域名
  return `${apiDomain}${imageUrl}`;
};

const handleSubmit = async () => {
  if (!formRef.value) return;

  await formRef.value.validate(async (valid) => {
    if (valid) {
      try {
        saving.value = true;
        if (contactId.value) {
          await updateCustomerServiceContact(contactId.value, formData);
          ElMessage.success('更新成功');
        } else {
          await addCustomerServiceContact(formData);
          ElMessage.success('保存成功');
          // 重新获取数据以获取新的ID
          await fetchContact();
        }
      } catch (error) {
        ElMessage.error(error.message || '操作失败');
      } finally {
        saving.value = false;
      }
    }
  });
};

onMounted(() => {
  fetchContact();
});
</script>

<style scoped>
.customer-service-contact-list {
  padding: 20px;
}

.page-title {
  margin-bottom: 20px;
  font-size: 24px;
  font-weight: bold;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-content {
  padding: 20px 0;
}

.upload-demo {
  margin-bottom: 20px;
}

/* 隐藏上传区域的边框和背景 */
:deep(.hide-upload-area .el-upload--picture-card) {
  display: none;
}
</style>
